
import React, { useState, useEffect, useRef } from 'react';
import emailjs from '@emailjs/browser';
import '../App.css';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import BackToTopButton from '../components/BackToTopButton';
import QAChatbot from '../components/QAChatBot';
import LiveChat from '../components/LiveChat';
import ScrollSocialIcons from '../components/ScrollSocialIcons';

const countryInfo = {
  'Sri Lanka': { code: '+94', iso: 'LK', digitLength: 9 },
  'India': { code: '+91', iso: 'IN', digitLength: 10 },
  'USA': { code: '+1', iso: 'US', digitLength: 10 },
  'UK': { code: '+44', iso: 'GB', digitLength: 10 },
  'UAE': { code: '+971', iso: 'AE', digitLength: 9 },
  'China': { code: '+86', iso: 'CN', digitLength: 11 },
  'South Korea': { code: '+82', iso: 'KR', digitLength: 9 },
  'Japan': { code: '+81', iso: 'JP', digitLength: 10 },
  'Singapore': { code: '+65', iso: 'SG', digitLength: 8 },
  'Germany': { code: '+49', iso: 'DE', digitLength: 10 },
};

const countryFlags = {
  'Sri Lanka': '🇱🇰',
  'India': '🇮🇳',
  'USA': '🇺🇸',
  'UK': '🇬🇧',
  'UAE': '🇦🇪',
  'China': '🇨🇳',
  'South Korea': '🇰🇷',
  'Japan': '🇯🇵',
  'Singapore': '🇸🇬',
  'Germany': '🇩🇪'
};

const bankOptions = {
  'Sri Lanka': ['BOC', 'Peoples Bank', 'Commercial Bank', 'HNB'],
  'India': ['State Bank of India', 'ICICI Bank', 'Axis Bank'],
  'USA': ['Chase', 'Bank of America', 'Wells Fargo'],
  'UK': ['HSBC', 'Barclays', 'Lloyds Bank'],
  'UAE': ['Emirates NBD', 'First Abu Dhabi Bank (FAB)', 'Mashreq Bank', 'Abu Dhabi Commercial Bank (ADCB)'],
  'China': ['Industrial and Commercial Bank of China (ICBC)', 'Bank of China', 'China Construction Bank', 'Agricultural Bank of China'],
  'South Korea': ['Kookmin Bank (KB)', 'Shinhan Bank', 'Hana Bank', 'Woori Bank'],
  'Japan': ['Mizuho Bank', 'MUFG Bank', 'Sumitomo Mitsui Banking Corporation (SMBC)', 'Resona Bank'],
  'Singapore': ['DBS Bank', 'OCBC Bank', 'United Overseas Bank (UOB)', 'Standard Chartered Singapore'],
  'Germany': ['Deutsche Bank', 'Commerzbank', 'DZ Bank', 'UniCredit Bank (HypoVereinsbank)']
};

const creditOptions = ['30', '60', '90'];

const categoryOptions = [
  { label: 'A - Engineering/General Engineering/Plant/Machinery/Tools' },
  { label: 'B - Electrical/Electronics/Communication' },
  { label: 'C \u2013 Stationery Items' },
  { 
    label: 'D - Insurance a- Vehicle',
    options: ['D-a-Car', 'D-a-Van', 'D-a-Lorry', 'D-a-Jeep', 'D-a-Motorbike']
  },
  { 
    label: 'D - Insurance b- Life',
    options: ['D-b-Medical', 'D-b-Major Illness', 'D-b-Accidents', 'D-b-Workmen Composition', 'D-b-OPD', 'D-b-Others']
  },
  { label: 'E - Specialize Services/Repairs/Consultancy' },
  { label: 'F - Building Materials & Hardware' },
  { label: 'G \u2013 Computers/Related Equipment/Software & IT Services' },
  { 
    label: 'H - Vehicle Repairs',
    options: ['H-Car', 'H-Van', 'H-Lorry', 'H-Jeep', 'H-Motorbike']
  },
  { 
    label: 'I- Freight',
    options: ['I-Sea', 'I-Air']
  }
];

const SupplierRegistrationForm = () => {
  const formRef = useRef();

  const [formData, setFormData] = useState({
    companyName: '',
    companyAddress: '',
    companyEmail: '',
    country: '',
    bankName: '',
    contactNumber: '',
    whatsappNumber: '',
    supplies: false,
    services: false,
    paymentTerms: '',
    creditPeriod: '',
    contactPersonName: '',
    contactPersonNumber: '',
    contactPersonEmail: '',
    category: '',
  });

  const [customBankName, setCustomBankName] = useState('');
  const [file, setFile] = useState(null);
  const [status, setStatus] = useState('');

  useEffect(() => {
    const code = countryInfo[formData.country]?.code || '';
    if (!code) return; // Guard if country is empty

    setFormData((prev) => {
      const updateNumber = (num) => {
        if (!num) return code;

        // Find if current number already starts with any known country code and strip it
        let stripped = num;
        Object.values(countryInfo).forEach(info => {
          if (num.startsWith(info.code)) {
            stripped = num.substring(info.code.length);
          }
        });

        const digitsOnly = stripped.replace(/\D/g, '');
        return code + digitsOnly;
      };

      return {
        ...prev,
        contactNumber: updateNumber(prev.contactNumber),
        whatsappNumber: updateNumber(prev.whatsappNumber),
        contactPersonNumber: updateNumber(prev.contactPersonNumber),
      };
    });
  }, [formData.country]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox') {
      setFormData({ ...formData, [name]: checked });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleFileChange = (e) => setFile(e.target.files[0]);

  const isValidPhoneNumber = (number, country) => {
    if (!country || !countryInfo[country]) return false;
    const code = countryInfo[country].code;
    const expectedLength = countryInfo[country].digitLength;
    const escapedCode = code.replace(/\+/g, '\\+');
    const digits = number.replace(new RegExp(`^${escapedCode}`), '').replace(/\D/g, '');
    console.log(`Validating ${number}: code=${code}, escapedCode=${escapedCode}, expectedLength=${expectedLength}, digits=${digits}, length=${digits.length}`);
    return number.startsWith(code) && digits.length === expectedLength;
  };

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateForm = () => {
    const requiredFields = [
      'companyName',
      'companyAddress',
      'companyEmail',
      'country',
      'contactNumber',
      'contactPersonName',
      'contactPersonNumber',
      'contactPersonEmail',
      'paymentTerms',
      'creditPeriod',
      'category'
    ];

    // Bank name is required if countries have banks
    if ((bankOptions[formData.country] || []).length > 0) {
      if (!formData.bankName) return { valid: false, message: 'Please select a Bank.' };
      if (formData.bankName === 'Other' && !customBankName.trim()) return { valid: false, message: 'Please enter custom bank name.' };
    }

    const allFilled = requiredFields.every(field => formData[field] && formData[field].toString().trim());
    const emailValid = isValidEmail(formData.companyEmail) && isValidEmail(formData.contactPersonEmail);
    const phoneValid = isValidPhoneNumber(formData.contactNumber, formData.country);
    const contactPhoneValid = isValidPhoneNumber(formData.contactPersonNumber, formData.country);

    // Optional whatsapp validation if it contains more than just the prefix
    const code = countryInfo[formData.country]?.code || '';
    const whatsappValid = !formData.whatsappNumber || formData.whatsappNumber === code || isValidPhoneNumber(formData.whatsappNumber, formData.country);

    console.log('Validation:', { allFilled, emailValid, phoneValid, contactPhoneValid, whatsappValid });

    if (!allFilled) return { valid: false, message: 'Please fill all required fields.' };
    if (!emailValid) return { valid: false, message: 'Please enter valid email addresses.' };
    if (!phoneValid || !contactPhoneValid) return { valid: false, message: `Ensure contact numbers are valid (${countryInfo[formData.country]?.digitLength} digits with country code).` };
    if (formData.whatsappNumber && formData.whatsappNumber !== code && !whatsappValid) return { valid: false, message: 'Please enter a valid WhatsApp number.' };

    return { valid: true, message: '' };
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const validation = validateForm();
    if (!validation.valid) {
      return setStatus(`❗ ${validation.message}`);
    }

    let attachmentInfo = '';
    if (file) {
      const fileSizeKB = file.size / 1024;
      attachmentInfo = `Attachment: ${file.name} (Size: ${fileSizeKB.toFixed(2)} KB) - Please request separately due to size limit.`;
    }

    const formDataToSend = {
      ...formData,
      bankName: formData.bankName === 'Other' ? customBankName : formData.bankName,
      attachmentInfo: attachmentInfo || 'No attachment'
    };

    console.log('Submitting form data:', formDataToSend);
    emailjs.send('service_v7xwleq', 'template_473kmjx', formDataToSend, 'nY-PyzSMsXXjQl2Id')
      .then(() => setStatus('✅ Form submitted successfully! Attachment will be handled separately.'))
      .catch((error) => {
        console.error('EmailJS error:', error);
        setStatus('❌ Failed to send the form. Please try again.');
      });
  };

  return (
    <div className="supplier-page">
      <div className="video-background">
        <video autoPlay muted loop playsInline>
          <source src="/images/srform.mp4" type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      </div>
      <Navbar />
      <div className="form-container">
        <h2 className="form-title">Supplier / Service Provider Registration</h2>

        <form ref={formRef} onSubmit={handleSubmit} className="supplier-form">
          <div className="form-row">
            <input type="text" name="companyName" value={formData.companyName} onChange={handleChange} required placeholder="Company Name" />
          </div>

          <div className="form-row">
            <input type="text" name="companyAddress" value={formData.companyAddress} onChange={handleChange} required placeholder="Company Address" />
          </div>

          <div className="form-row">
            <input type="email" name="companyEmail" value={formData.companyEmail} onChange={handleChange} required placeholder="Company Email" />
          </div>

          <div className="form-group-grid">
            {formData.country && (
              <img
                src={`https://flagcdn.com/w40/${countryInfo[formData.country]?.iso.toLowerCase()}.png`}
                alt={formData.country}
                className="country-flag"
              />
            )}
            <select name="country" value={formData.country} onChange={handleChange} required>
              <option value="">Select Country</option>
              {Object.keys(bankOptions).map((country, i) => (
                <option key={i} value={country}>
                  {countryFlags[country]} {country}
                </option>
              ))}
            </select>

            {(bankOptions[formData.country] || []).length > 0 && (
              <>
                <select
                  name="bankName"
                  value={formData.bankName}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select Bank</option>
                  {bankOptions[formData.country].map((bank, i) => (
                    <option key={i} value={bank}>{bank}</option>
                  ))}
                  <option value="Other">Other</option>
                </select>

                {formData.bankName === 'Other' && (
                  <input
                    type="text"
                    name="customBank"
                    placeholder="Enter Bank Name"
                    value={customBankName}
                    onChange={(e) => setCustomBankName(e.target.value)}
                    required
                  />
                )}
              </>
            )}
          </div>

          <div className="form-group-grid">
            <div className="input-with-flag">
              <span className="flag-emoji">
                {formData.country && (
                  <img
                    src={`https://flagcdn.com/24x18/${countryInfo[formData.country]?.iso.toLowerCase()}.png`}
                    alt={formData.country + ' flag'}
                    style={{ width: '24px', height: '18px', marginRight: '5px' }}
                  />
                )}
              </span>
              <input
                type="tel"
                name="contactNumber"
                value={formData.contactNumber}
                onChange={handleChange}
                required
                placeholder="Contact Number"
                maxLength={15}
              />
            </div>

            <div className="input-with-flag">
              <span className="flag-emoji">
                {formData.country && (
                  <img
                    src={`https://flagcdn.com/24x18/${countryInfo[formData.country]?.iso.toLowerCase()}.png`}
                    alt={formData.country + ' flag'}
                    style={{ width: '24px', height: '18px', marginRight: '5px' }}
                  />
                )}
              </span>
              <input
                type="tel"
                name="whatsappNumber"
                value={formData.whatsappNumber}
                onChange={handleChange}
                placeholder="Whatsapp Number"
                maxLength={15}
              />
            </div>
          </div>

          <span><b>Additional Information</b></span>
          <div className="form-row">
            <input
              type="text"
              name="contactPersonName"
              value={formData.contactPersonName}
              onChange={handleChange}
              placeholder="Contact Person Name"
              required
            />
          </div>

          <div className="form-group-grid">
            <input
              type="tel"
              name="contactPersonNumber"
              value={formData.contactPersonNumber}
              onChange={handleChange}
              placeholder="Contact Person Phone Number"
              maxLength={15}
              required
            />
            <input
              type="email"
              name="contactPersonEmail"
              value={formData.contactPersonEmail}
              onChange={handleChange}
              placeholder="Contact Person Email"
              required
            />
          </div>

          <div className="form-group-grid">
            <div className="form-row">
              <span><b>Suppliers / Services:</b></span>
              <div style={{ display: 'flex', gap: '15px', marginTop: '5px' }}>
                <label><input type="checkbox" name="supplies" checked={formData.supplies} onChange={handleChange} /> Supplies</label>
                <label><input type="checkbox" name="services" checked={formData.services} onChange={handleChange} /> Services</label>
              </div>
            </div>
            <div className="form-row">
              <span><b>Category:</b></span>
              <select name="category" value={formData.category} onChange={handleChange} required>
                <option value="">Select Category</option>
                {categoryOptions.map((cat, i) => (
                  cat.options ? (
                    <optgroup key={i} label={cat.label}>
                      {cat.options.map((opt, j) => (
                        <option key={j} value={`${cat.label} - ${opt}`}>{opt}</option>
                      ))}
                    </optgroup>
                  ) : (
                    <option key={i} value={cat.label}>{cat.label}</option>
                  )
                ))}
              </select>
            </div>
          </div>

          <div className="form-row">
            <span><b>Catalogs / Brochures / Portfolios:</b></span>
            <input type="file" name="attachment" onChange={handleFileChange} />
            <p style={{ fontSize: '12px' }}>*Please upload less than 1MB PDF</p>
          </div>

          <div>
            <span><b>Payment Terms:</b></span>
            <label><input type="radio" name="paymentTerms" value="cash" checked={formData.paymentTerms === 'cash'} onChange={handleChange} /> Bank Transfers</label>
            <label><input type="radio" name="paymentTerms" value="online" checked={formData.paymentTerms === 'online'} onChange={handleChange} /> Online Transfer</label>
          </div>

          <div>
            <span><b>Credit Period:</b></span>
            {creditOptions.map((day) => (
              <label key={day}>
                <input
                  type="radio"
                  name="creditPeriod"
                  value={day}
                  checked={formData.creditPeriod === day}
                  onChange={handleChange}
                /> {day} days
              </label>
            ))}
          </div>

          <button type="submit" className="submit-btn">Submit</button>
          {status && <p className="status-message">{status}</p>}
        </form>
      </div>
      <QAChatbot />
      <LiveChat />
      <ScrollSocialIcons />
      <BackToTopButton />
      <Footer />
    </div>
  );
};

export default SupplierRegistrationForm;
